import { useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,      
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT, DIVERT_RETURN_STATUS, MAXIMUM_TAT_MINUTES } from 'state/constants';
import { filterFlights } from 'state/helpers';
import { countValuesByField, countAvgMinutesByTableau } from './utils';
import Header from 'components/Header';
import FilterButton from 'components/FilterButton';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import ChartDialog from 'components/chartNivo/ChartDialog';
import FlightFilterDialog from 'components/FlightFilterDialog';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import CommonTableau from './CommonTableau';
import { boxStyles as styles } from 'components/CustomStyles';


const displayOptions = [
    { value: "chart", label: "Chart" },
    { value: "tableau", label: "Tableau" },
];

const tableauViewOptions = [
    { value: "byDate", label: "by Date" },
    { value: "byDateDep", label: "by Date & Port" },
    { value: "byDateSector", label: "by Date & Sector" },
    { value: "byDateSectorType", label: "by Date & Sector Type" },
    { value: "byDateFlightType", label: "by Date & Flight Type" },
    { value: "byDateFlightReg", label: "by Date & Flight Reg" },
    { value: "byDateACType", label: "by Date & AC Type" },
    { value: "byDateCountry", label: "by Date & Country" },
    { value: "byDateFlightNo", label: "by Date & Flight No" },
    { value: "byDep", label: "by Port" },
    { value: "bySector", label: "by Sector" },
    { value: "bySectorType", label: "by Sector Type" },
    { value: "byFlightType", label: "by Flight Type" },
    { value: "byFlightReg", label: "by Flight Reg" },
    { value: "byFlightRegDep", label: "by Reg & Port" },
    { value: "byFlightRegSector", label: "by Reg & Sector" },
    { value: "byFlightRegSectorType", label: "by Reg & Sector Type" },
    { value: "byFlightRegFlightType", label: "by Reg & Flight Type" },
    { value: "byFlightRegCountry", label: "by Reg & Country" },
    { value: "byACType", label: "by AC Type" },
    { value: "byACTypeDep", label: "by AC & Port" },
    { value: "byACTypeSector", label: "by AC & Sector" },
    { value: "byACTypeSectorType", label: "by AC & Sector Type" },
    { value: "byACTypeFlightType", label: "by AC & Flight Type" },
    { value: "byACTypeCountry", label: "by AC & Country" },
    { value: "byCountry", label: "by Country" },
    { value: "byFlightNo", label: "by Flight No" },
];

const viewOptions = {
    chart: [
        { value: "tatByFlightDate", label: "by Date" },
        { value: "tatByDep", label: "by Port" },
        { value: "tatBySector", label: "by Sector" },
        { value: "tatBySectorType", label: "by Sector Type" },
        { value: "tatByFlightType", label: "by Flight Type" },
        { value: "tatByFlightReg", label: "by Flight Reg" },
        { value: "tatByACType", label: "by AC Type" },
        { value: "tatByCountry", label: "by Country" },
        { value: "tatByFlightNo", label: "by Flight No" },
    ],
    tableau: tableauViewOptions,
};

const TATChart = () => {    
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
        
    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [chartOpen, setChartOpen] = useState(false);  
    
    const [tatType, setTatType] = useState('actual');
    const [displayType, setDisplayType] = useState('chart');
    const [view, setView] = useState('tatByFlightDate');

    const flightsData = useSelector((state) => state.flights); 
    const flights = useMemo(() => {
        const processedFlights = flightsData.filter(
            f =>
                !f.flightStatus?.toLowerCase().includes('cancelled') &&
                !f.flightStatus?.toLowerCase().includes('deleted') &&
                !DIVERT_RETURN_STATUS.includes(f.flightStatus)
        );
        
        return filterFlights({
            flights: processedFlights,
            filters,
            timeMode
        });
    }, [flightsData, filters, timeMode]);

    const tat = useMemo(() => {
        switch (tatType) {
            case 'actual':  
                return 'actualTat';
            case 'schedule':    
                return 'scheduleTat';
            case 'estimated':   
                return 'estimatedTat';
            default:    
                return 'tat';
        }   
    }, [tatType]); 

    const flightDate = timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase';

    const safeView = useMemo(() => {
        const options = viewOptions[displayType] || [];
        return options.some(o => o.value === view) ? view : options[0]?.value || '';
    }, [view, displayType]);

    const chartData = useMemo(() => {
        if (displayType !== 'chart') return [];
        const fieldMap = {
            tatByFlightDate: flightDate,
            tatByDep: 'dep',
            tatBySector: 'sector',
            tatBySectorType: 'sectorType',
            tatByFlightType: 'flightType',
            tatByFlightReg: 'flightReg',
            tatByACType: 'acType',
            tatByCountry: 'depCountryName',
            tatByFlightNo: 'flightNo',
        };
        const field = fieldMap[safeView];
        return field ? countValuesByField(flights, tat, field) : [];
    }, [displayType, safeView, flightDate, flights, tat]);

    const tableauData = useMemo(() => {
        if (displayType !== 'tableau') return [];
        const f = flights || [];
        const fieldMap = {
            byDate:                [flightDate, null],
            byDateDep:             [flightDate, 'dep'],
            byDateSector:          [flightDate, 'sector'],
            byDateSectorType:      [flightDate, 'sectorType'],
            byDateFlightType:      [flightDate, 'flightType'],
            byDateFlightReg:       [flightDate, 'flightReg'],
            byDateACType:          [flightDate, 'acType'],
            byDateCountry:         [flightDate, 'depCountryName'],
            byDateFlightNo:        [flightDate, 'flightNo'],
            byDep:                 ['dep', null],
            bySector:              ['sector', null],
            bySectorType:          ['sectorType', null],
            byFlightType:          ['flightType', null],
            byFlightReg:           ['flightReg', null],
            byFlightRegDep:        ['flightReg', 'dep'],
            byFlightRegSector:     ['flightReg', 'sector'],
            byFlightRegSectorType: ['flightReg', 'sectorType'],
            byFlightRegFlightType: ['flightReg', 'flightType'],
            byFlightRegCountry:    ['flightReg', 'depCountryName'],
            byACType:              ['acType', null],
            byACTypeDep:           ['acType', 'dep'],
            byACTypeSector:        ['acType', 'sector'],
            byACTypeSectorType:    ['acType', 'sectorType'],
            byACTypeFlightType:    ['acType', 'flightType'],
            byACTypeCountry:       ['acType', 'depCountryName'],
            byCountry:             ['depCountryName', null],
            byFlightNo:            ['flightNo', null],
        };
        const fields = fieldMap[safeView];
        if (!fields) return [];
        return countAvgMinutesByTableau(f, fields[0], fields[1], tat, 'TAT', (_, v) => v && v < MAXIMUM_TAT_MINUTES);
    }, [displayType, safeView, flightDate, flights, tat]);

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);

    const handleFilterApply = (filterValues) => {
        setFilters(filterValues);
    };

    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(flights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(flights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [flights]);
    
    const handleOpen = () => {
        setChartOpen(true);
    };

    const handleClose = () => {
        setChartOpen(false);
    };
    
    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `TAT ${period}`;

    const chartProps = useMemo(() => ({
        tatByFlightDate: { bottomLegend: "Date", leftLegend: "Minutes", digit: 0, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 },
        tatByDep: { bottomLegend: "Port", leftLegend: "Minutes", digit: 0, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 },
        tatBySector: { bottomLegend: "Sector", leftLegend: "Minutes", digit: 0, bottomOffSet: 10, bottomLegendOffSet: 10, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 },
        tatBySectorType: { bottomLegend: "Sector Type", leftLegend: "Minutes", isHorizontalBottomLegend: true, digit: 0, bottomOffSet: -30, bottomLegendOffSet: -30, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 },
        tatByFlightType: { bottomLegend: "Flight Type", leftLegend: "Minutes", isHorizontalBottomLegend: !isMobile, digit: 0, bottomOffSet: isMobile ? 50 : -30, bottomLegendOffSet: isMobile ? 50 : -30, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 },
        tatByFlightReg: { bottomLegend: "Flight Reg", leftLegend: "Minutes", digit: 0, bottomOffSet: 5, bottomLegendOffSet: 5, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 },
        tatByACType: { bottomLegend: "AC Type", leftLegend: "Minutes", digit: 0, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 },
        tatByCountry: { bottomLegend: "Country", leftLegend: "Minutes", digit: 0, bottomOffSet: isMobile ? 55 : 45, bottomLegendOffSet: 45, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 },
        tatByFlightNo: { bottomLegend: "Flight No", leftLegend: "Minutes", digit: 0, bottomOffSet: 5, bottomLegendOffSet: 5, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 },
    }), [isMobile]);

    const renderChart = (view, customStyles) => {
        const props = chartProps[view];
        return props ? (
          <Box sx={{ ...styles, ...customStyles }}>
            <SortBarMultiLinesChart data={chartData} title={defaultTitle} {...props} />
          </Box>
        ) : (
          <Box sx={{ ...styles, ...customStyles }}>
            <Typography>No charts available</Typography>
          </Box>
        );
    };

    
    return (
        <Box className="main-container-box">                  
            <FlexBetween>
                <Header
                    title="Turnaround Time"
                    subtitle={displayType === 'chart' ? 'List of Charts' : 'List of Tableaus'}
                />
                <Box display="flex" alignItems="center" gap={1}>
                    {!isMobile && (
                        <FormControl 
                            size="small" 
                            sx={{ width: { xs: '6rem', sm: '9rem' } }}
                        >
                            <InputLabel>Type</InputLabel>
                            <Select
                                label="Type"
                                value={tatType}
                                onChange={(e) => setTatType(e.target.value)}
                            >
                                <MenuItem value="actual">Actual</MenuItem>
                                <MenuItem value="schedule">Schedule</MenuItem>
                                <MenuItem value="estimated">Estimated</MenuItem>
                            </Select>
                        </FormControl>
                    )}

                    <FormControl 
                        size="small" 
                        sx={{ width: isMobile ? '6rem' : '8rem' }}
                    >
                        <InputLabel>Display</InputLabel>
                        <Select
                            value={displayType}
                            label="Display"
                            onChange={(e) => setDisplayType(e.target.value)}
                        >
                            {displayOptions.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl 
                        size="small" 
                        sx={{ width: isMobile ? '7rem' : '12rem' }}
                    >
                        <InputLabel id="tat-view-label">View</InputLabel>
                        <Select
                            labelId="tat-view-label"
                            label="View"
                            value={safeView}
                            onChange={(e) => setView(e.target.value)}
                        >
                            {viewOptions[displayType].map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FilterButton
                        filters={filters}
                        onClick={handleFilterOpen}
                    />
                </Box>
            </FlexBetween>             
            
            <Box
                display="grid"                        
                gridTemplateColumns="repeat(12, minmax(0, 1fr))"                                   
            >
                <Box
                    ref={scrollRef}
                    height={LAYOUT_HEIGHT}
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{ gridColumn: "span 12", overflowY: 'auto' }}
                    onDoubleClick={displayType === 'chart' ? handleOpen : undefined}
                >
                    {displayType === 'chart'
                        ? renderChart(safeView, { minHeight: LAYOUT_HEIGHT })
                        : <Box sx={{ ...styles, minHeight: LAYOUT_HEIGHT }}>
                                <Box height="100%" width="100%">
                                    <CommonTableau data={tableauData} />
                                </Box>
                          </Box>
                    }
                </Box>

                <ScrollToTop scrollRef={scrollRef} />
            </Box>
        
            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }

            {chartOpen && displayType === 'chart' && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleClose}
                    chartComponent={renderChart(safeView, {})}
                />
            )}
        </Box>
    );
};

export default TATChart;
